import { useMemo } from "react";

import { AlertTriangle, ClipboardCopy, Radar } from "lucide-react";
import { toast } from "sonner";

import { CollapsibleCard } from "@/components/cinch/CollapsibleCard";
import { EmptyState } from "@/components/cinch/EmptyState";
import { KpiTile } from "@/components/cinch/KpiTile";
import { LinkOut } from "@/components/cinch/LinkOut";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtDateTime } from "@/lib/date";
import { MarkdownView } from "@/lib/markdown";
import { cn } from "@/lib/utils";

import type { KbRagSearch } from "..";
import {
  useKbScans,
  useSetScanItemState,
  type ScanGap,
  type ScanImpact,
  type ScanRun,
} from "../api";
import { ReviewStateControl } from "./ReviewStateControl";

const CONFIDENCE_ORDER = { high: 0, medium: 1, low: 2 } as const;

function shortDay(iso: string | null): string {
  if (!iso) return "?";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso.slice(0, 10)
    : d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

async function copyText(text: string, what: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied`);
  } catch {
    toast.error("Could not copy — select the text and copy it by hand");
  }
}

function ImpactCard({
  impact,
  canAct,
  onOpenArticle,
}: {
  impact: ScanImpact;
  canAct: boolean;
  onOpenArticle: (documentId: string) => void;
}) {
  const save = useSetScanItemState();
  return (
    <Card className={cn(impact.status === "done" && "opacity-70")}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <LinkOut href={impact.url} className="text-base font-semibold">
                {impact.title ?? "Untitled article"}
              </LinkOut>
              <StatusBadge
                value={impact.confidence}
                label={`${impact.confidence} confidence`}
              />
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {impact.category ? <span>{impact.category}</span> : null}
              {impact.document_id ? (
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs"
                  onClick={() => onOpenArticle(impact.document_id ?? "")}
                >
                  view indexed article
                </Button>
              ) : null}
            </div>
          </div>
          <StatusBadge value={`review_${impact.status}`} />
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {impact.why ? <p className="text-sm">{impact.why}</p> : null}
        {impact.suggested_action ? (
          <p className="text-sm">
            <span className="font-medium">Suggested:</span>{" "}
            {impact.suggested_action}
          </p>
        ) : null}
        {impact.changes.length > 0 ? (
          <CollapsibleCard
            bare
            title={
              <span className="text-xs font-medium">
                Code changes behind this
              </span>
            }
            meta={`${impact.changes.length} change${impact.changes.length === 1 ? "" : "s"}`}
            contentClassName="pt-1"
          >
            <ul className="flex flex-col gap-1.5 text-xs">
              {impact.changes.map((c, i) => (
                <li key={i} className="flex flex-col">
                  <span>{c.summary ?? "(no summary)"}</span>
                  <span className="font-mono text-muted-foreground">
                    {c.repo ? `${c.repo}/` : ""}
                    {c.file_path ?? ""}
                    {c.commit ? ` @ ${c.commit.slice(0, 7)}` : ""}
                    {c.author ? ` · ${c.author}` : ""}
                    {c.date ? ` · ${c.date.slice(0, 10)}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </CollapsibleCard>
        ) : null}
        <ReviewStateControl
          itemKey={impact.id}
          state={impact}
          canAct={canAct}
          saving={save.isPending}
          compact
          onSave={({ status, note }) =>
            save.mutate(
              { kind: "scan_impact", itemId: impact.id, status, note },
              {
                onSuccess: () => toast.success("Saved"),
                onError: (e) => toast.error(String(e)),
              },
            )
          }
        />
      </CardContent>
    </Card>
  );
}

function GapCard({ gap, canAct }: { gap: ScanGap; canAct: boolean }) {
  const save = useSetScanItemState();
  return (
    <Card className={cn(gap.status === "done" && "opacity-70")}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-base">{gap.capability}</CardTitle>
              <StatusBadge
                value={gap.coverage === "none" ? "no_coverage" : "weak"}
              />
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="outline" className="text-[10px]">
                {gap.source_kind === "mcp_tool"
                  ? "agent tool"
                  : gap.source_kind === "route"
                    ? "app screen"
                    : "capability"}
              </Badge>
              {gap.source_name ? (
                <span className="font-mono">{gap.source_name}</span>
              ) : null}
            </div>
          </div>
          <StatusBadge value={`review_${gap.status}`} />
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {gap.nearest_articles.length > 0 ? (
          <div className="text-xs">
            <span className="text-muted-foreground">
              Closest existing articles:{" "}
            </span>
            {gap.nearest_articles.map((a, i) => (
              <span key={i}>
                {i > 0 ? ", " : ""}
                <LinkOut href={a.url} size="xs">
                  {a.title ?? a.document_id ?? "article"}
                </LinkOut>
                {a.score !== null ? (
                  <span className="text-muted-foreground">
                    {" "}
                    ({a.score.toFixed(2)})
                  </span>
                ) : null}
              </span>
            ))}
          </div>
        ) : null}
        {gap.outline ? (
          <div className="rounded-md bg-muted/50 p-3">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Draft outline
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => copyText(gap.outline ?? "", "Outline")}
              >
                <ClipboardCopy className="mr-1 h-3 w-3" /> Copy
              </Button>
            </div>
            <MarkdownView source={gap.outline} className="text-sm" />
          </div>
        ) : null}
        <ReviewStateControl
          itemKey={gap.id}
          state={gap}
          canAct={canAct}
          saving={save.isPending}
          compact
          onSave={({ status, note }) =>
            save.mutate(
              { kind: "scan_gap", itemId: gap.id, status, note },
              {
                onSuccess: () => toast.success("Saved"),
                onError: (e) => toast.error(String(e)),
              },
            )
          }
        />
      </CardContent>
    </Card>
  );
}

export function ReleaseImpactTab({
  search,
  setSearch,
  canAct,
}: {
  search: KbRagSearch;
  setSearch: (next: Partial<KbRagSearch>) => void;
  canAct: boolean;
}) {
  const { data, isPending, error } = useKbScans();
  const scans = useMemo(() => data?.scans ?? [], [data]);
  const scan: ScanRun | undefined = useMemo(
    () =>
      (search.scan
        ? scans.find((s) => s.scan_id === search.scan)
        : undefined) ?? scans[0],
    [scans, search.scan],
  );

  if (isPending) return <Skeleton className="h-96" />;

  if (error) {
    return (
      <Alert variant="danger">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Could not load release scans</AlertTitle>
        <AlertDescription>
          {error instanceof Error ? error.message : String(error)}
        </AlertDescription>
      </Alert>
    );
  }

  if (!data.available) {
    return (
      <Alert variant="warning">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Release scans are unavailable</AlertTitle>
        <AlertDescription>
          The pipeline&rsquo;s status API could not be reached
          {data.reason ? ` (${data.reason})` : ""}. When it is running, every
          Thursday at 03:00 MT the week&rsquo;s code changes are mapped onto the
          articles they probably affect and land here.
        </AlertDescription>
      </Alert>
    );
  }

  if (!scan) {
    return (
      <EmptyState icon={<Radar />} title="No release scans yet">
        The first scan runs on the Thursday after the reviewer is deployed. It
        will list the articles the week&rsquo;s code changes probably affect,
        and the capabilities that have no article yet.
      </EmptyState>
    );
  }

  const impacts = [...scan.impacts].sort(
    (a, b) => CONFIDENCE_ORDER[a.confidence] - CONFIDENCE_ORDER[b.confidence],
  );
  const highCount = impacts.filter((i) => i.confidence === "high").length;
  const filesChanged = scan.repos.reduce(
    (s, r) => s + (r.files_changed ?? 0),
    0,
  );
  const openItems = [...scan.impacts, ...scan.gaps].filter(
    (i) => i.status === "new",
  ).length;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">
            Code changed {shortDay(scan.window_since)} –{" "}
            {shortDay(scan.window_until)}
          </h3>
          <p className="text-xs text-muted-foreground">
            Scanned {fmtDateTime(scan.scanned_at)}
            {scan.triage_model ? ` · triage ${scan.triage_model}` : ""}
            {scan.judge_model ? ` · judged by ${scan.judge_model}` : ""}
            {scan.cost_usd !== null ? ` · $${scan.cost_usd.toFixed(2)}` : ""}
          </p>
        </div>
        {scans.length > 1 ? (
          <Select
            value={scan.scan_id}
            onValueChange={(v) => setSearch({ scan: v })}
          >
            <SelectTrigger className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {scans.map((s) => (
                <SelectItem key={s.scan_id} value={s.scan_id}>
                  {shortDay(s.window_since)} – {shortDay(s.window_until)}
                  <span className="ml-2 text-xs text-muted-foreground">
                    {s.impacts.length} affected · {s.gaps.length} gaps
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label="Commits scanned"
          value={scan.commits_total?.toLocaleString() ?? "—"}
          sub={`${filesChanged.toLocaleString()} files across ${scan.repos.length} repo${scan.repos.length === 1 ? "" : "s"}`}
        />
        <KpiTile
          label="Articles likely affected"
          value={impacts.length}
          sub={`${highCount} high confidence`}
          tone={highCount ? "warning" : "neutral"}
        />
        <KpiTile
          label="Gaps"
          value={scan.gaps.length}
          sub="capabilities with no article"
          tone={scan.gaps.length ? "warning" : "positive"}
        />
        <KpiTile
          label="Still to look at"
          value={openItems}
          sub="rows not yet accepted, done or rejected"
          tone={openItems ? "warning" : "positive"}
        />
      </div>

      {scan.repos.length > 0 ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              What changed, by repository
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
              {scan.repos.map((r) => (
                <li key={r.repo} className="tabular-nums">
                  <span className="font-mono">{r.repo}</span>
                  <span className="text-muted-foreground">
                    {" "}
                    · {r.commits ?? "?"} commits · {r.files_changed ?? "?"}{" "}
                    files
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      <section className="flex flex-col gap-3">
        <h4 className="text-sm font-semibold">
          Articles probably affected{" "}
          <span className="font-normal text-muted-foreground">
            — highest confidence first
          </span>
        </h4>
        {impacts.length === 0 ? (
          <EmptyState
            bare
            title="No articles affected"
            className="rounded-lg border border-dashed"
          >
            Nothing that changed this week touches a documented feature.
          </EmptyState>
        ) : (
          impacts.map((i) => (
            <ImpactCard
              key={i.id}
              impact={i}
              canAct={canAct}
              onOpenArticle={(id) =>
                setSearch({ tab: "articles", article: id })
              }
            />
          ))
        )}
      </section>

      <section className="flex flex-col gap-3">
        <h4 className="text-sm font-semibold">
          Gaps{" "}
          <span className="font-normal text-muted-foreground">
            — capabilities with no article, with a draft outline
          </span>
        </h4>
        {scan.gaps.length === 0 ? (
          <EmptyState
            bare
            title="No gaps found"
            className="rounded-lg border border-dashed"
          >
            Every capability the scan checked has at least one article covering
            it.
          </EmptyState>
        ) : (
          scan.gaps.map((g) => <GapCard key={g.id} gap={g} canAct={canAct} />)
        )}
      </section>
    </div>
  );
}
